import React, { useState, useMemo, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Chip,
  InputAdornment,
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Pagination,
  useTheme,
  alpha,
  CircularProgress,
  Stack,
  Divider,
  LinearProgress
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  Search,
  CalendarToday,
  Payment,
  Undo,
  Person,
  TrendingUp,
  Close,
  Description,
  Group,
  CloudDownload,
  Category,
  Assignment,
  Receipt as ReceiptIcon,
  MonetizationOn as MoneyIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as BalanceIcon,
  CheckCircle as ApproveIcon,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell } from 'recharts';
import dayjs from 'dayjs';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { formatCurrency, formatWithOutSom } from '../Message';
import { DayIncome } from '../BaseURL';
import Swal from 'sweetalert2';
import axios from 'axios';


const getPaymentModeColor = (mode) => {
  const colors = {
    'Cash': '#1976d2',
    'cash': '#1976d2',
    'mobile_money': '#388e3c',
    'Mobile Money':'#388e3c',
    'credit': '#f57c00',
    'Credit': '#f57c00',
    'Transaction Reversal': '#d32f2f'
  };
  return colors[mode] || '#757575';
};

const getIncomeTypeColor = (type) => {
  const colors = {
    'service charge': '#2196f3',
    'laboratory charges': '#4caf50',
    'investigation charge': '#f44336',
    'other services': '#9c27b0',
    'medication': '#e91e63',
    'debtors payment':'#f321e9ff',
    'transaction reversal': '#d32f2f'
  };
  return colors[type] || '#757575';
};

// ─── Petty Expense Balance Card ───────────────────────────────────────
function PettyBalanceSection({ netTotal, pettyExpenses, pettyLoading }) {
  const PRIMARY      = '#11998e';
  const PRIMARY_DARK = '#0d7a71';
  const GHS          = 'GH₵';

  const approvedExpenses  = (pettyExpenses || []).filter(function(e) { return e.status === 'approved'; });
  const pendingExpenses   = (pettyExpenses || []).filter(function(e) { return e.status === 'pending'; });
  const totalPetty        = approvedExpenses.reduce(function(s, e) { return s + parseFloat(e.amount || 0); }, 0);
  const totalPending      = pendingExpenses.reduce(function(s, e)  { return s + parseFloat(e.amount || 0); }, 0);
  const balanceOff        = netTotal - totalPetty;

  function fmt(val) {
    return parseFloat(val || 0).toLocaleString('en-GH', { minimumFractionDigits: 2 });
  }

  return (
    <Card elevation={3} sx={{ borderRadius: 3, mt: 3, overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0d7a71 0%, #11998e 60%, #1abc9c 100%)',
        px: 3, py: 2
      }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <ReceiptIcon sx={{ color: '#fff', fontSize: 24 }} />
            <Box>
              <Typography fontWeight={800} color="#fff" fontSize="1rem">
                Petty Cash Expenses &amp; Balance Off
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.7rem' }}>
                Approved petty expenses deducted from net revenue
              </Typography>
            </Box>
          </Stack>
          {pettyLoading && <CircularProgress size={20} sx={{ color: '#fff' }} />}
        </Stack>
      </Box>

      {pettyLoading && (
        <LinearProgress sx={{ height: 3, '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg,#11998e,#38ef7d)' } }} />
      )}

      <CardContent sx={{ p: 3 }}>
        <Grid container spacing={3}>

          {/* ── Net Revenue (from billing) ── */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={0} sx={{
              p: 2.5, borderRadius: 2.5, textAlign: 'center',
              border: '1.5px solid', borderColor: alpha('#11998e', 0.2),
              background: alpha('#11998e', 0.04)
            }}>
              <Stack alignItems="center" spacing={0.8}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, background: alpha('#11998e', 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <MoneyIcon sx={{ color: '#11998e', fontSize: 22 }} />
                </Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}
                  sx={{ textTransform: 'uppercase', letterSpacing: '0.8px', fontSize: '0.6rem' }}>
                  Net Revenue
                </Typography>
                <Typography fontWeight={800} fontSize="1.1rem" sx={{ color: '#0d7a71' }}>
                  {GHS} {fmt(netTotal)}
                </Typography>
                <Typography variant="caption" color="text.disabled" fontSize="0.62rem">From billing</Typography>
              </Stack>
            </Paper>
          </Grid>

          {/* ── Approved Petty Expenses ── */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={0} sx={{
              p: 2.5, borderRadius: 2.5, textAlign: 'center',
              border: '1.5px solid', borderColor: alpha('#e05252', 0.2),
              background: alpha('#e05252', 0.04)
            }}>
              <Stack alignItems="center" spacing={0.8}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, background: alpha('#e05252', 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <TrendingDownIcon sx={{ color: '#e05252', fontSize: 22 }} />
                </Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}
                  sx={{ textTransform: 'uppercase', letterSpacing: '0.8px', fontSize: '0.6rem' }}>
                  Petty Expenses
                </Typography>
                <Typography fontWeight={800} fontSize="1.1rem" sx={{ color: '#c0392b' }}>
                  – {GHS} {fmt(totalPetty)}
                </Typography>
                <Typography variant="caption" color="text.disabled" fontSize="0.62rem">
                  {approvedExpenses.length} approved item{approvedExpenses.length !== 1 ? 's' : ''}
                </Typography>
              </Stack>
            </Paper>
          </Grid>

          {/* ── Pending petty expenses ── */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={0} sx={{
              p: 2.5, borderRadius: 2.5, textAlign: 'center',
              border: '1.5px solid', borderColor: alpha('#e6a817', 0.25),
              background: alpha('#e6a817', 0.04)
            }}>
              <Stack alignItems="center" spacing={0.8}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, background: alpha('#e6a817', 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ReceiptIcon sx={{ color: '#b37d0a', fontSize: 22 }} />
                </Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}
                  sx={{ textTransform: 'uppercase', letterSpacing: '0.8px', fontSize: '0.6rem' }}>
                  Pending Expenses
                </Typography>
                <Typography fontWeight={800} fontSize="1.1rem" sx={{ color: '#b37d0a' }}>
                  {GHS} {fmt(totalPending)}
                </Typography>
                <Typography variant="caption" color="text.disabled" fontSize="0.62rem">
                  {pendingExpenses.length} awaiting approval
                </Typography>
              </Stack>
            </Paper>
          </Grid>

          {/* ── Balance Off ── */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={0} sx={{
              p: 2.5, borderRadius: 2.5, textAlign: 'center',
              border: '2px solid',
              borderColor: balanceOff >= 0 ? alpha('#11998e', 0.4) : alpha('#e05252', 0.4),
              background: balanceOff >= 0
                ? 'linear-gradient(135deg, #0d7a71 0%, #11998e 100%)'
                : 'linear-gradient(135deg, #7c1f1f 0%, #e05252 100%)'
            }}>
              <Stack alignItems="center" spacing={0.8}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <BalanceIcon sx={{ color: '#fff', fontSize: 22 }} />
                </Box>
                <Typography variant="caption" fontWeight={700} sx={{ color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.8px', fontSize: '0.6rem' }}>
                  Balance Off
                </Typography>
                <Typography fontWeight={800} fontSize="1.15rem" color="#fff">
                  {GHS} {fmt(Math.abs(balanceOff))}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.62rem' }}>
                  {balanceOff >= 0 ? 'Surplus after expenses' : 'Deficit — expenses exceed revenue'}
                </Typography>
              </Stack>
            </Paper>
          </Grid>

        </Grid>

        {/* ── Breakdown table of approved expenses ── */}
        {approvedExpenses.length > 0 && (
          <Box mt={3}>
            <Divider sx={{ mb: 2 }}>
              <Chip label="Approved Expense Breakdown" size="small"
                sx={{ fontWeight: 700, fontSize: '0.7rem', color: PRIMARY, borderColor: alpha(PRIMARY, 0.3), border: '1px solid' }} />
            </Divider>
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: alpha(PRIMARY, 0.15), borderRadius: 2, overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { background: alpha(PRIMARY, 0.08), fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.6px', color: PRIMARY_DARK, py: 1.2 } }}>
                    <TableCell>Date</TableCell>
                    <TableCell>Paid To</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Approved By</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {approvedExpenses.map(function(exp, i) {
                    return (
                      <TableRow key={exp.id || i} sx={{ '&:hover': { background: alpha(PRIMARY, 0.03) }, '&:last-child td': { borderBottom: 'none' } }}>
                        <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{exp.expense_date}</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{exp.paid_to}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary', maxWidth: 200 }}>
                          <Typography variant="caption" noWrap sx={{ maxWidth: 190, display: 'block' }}>{exp.description || '—'}</Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.78rem' }}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <ApproveIcon sx={{ fontSize: 13, color: PRIMARY }} />
                            <Typography variant="caption" fontWeight={600}>{exp.approved_by || '—'}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={700} sx={{ color: '#c0392b', fontFamily: '"Courier New", monospace', fontSize: '0.85rem' }}>
                            {GHS} {parseFloat(exp.amount || 0).toLocaleString('en-GH', { minimumFractionDigits: 2 })}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Total row */}
                  <TableRow sx={{ background: alpha(PRIMARY, 0.06), '& td': { borderTop: '2px solid', borderTopColor: alpha(PRIMARY, 0.2) } }}>
                    <TableCell colSpan={4} sx={{ fontWeight: 800, fontSize: '0.82rem', color: PRIMARY_DARK }}>
                      TOTAL PETTY EXPENSES
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={800} sx={{ color: '#c0392b', fontFamily: '"Courier New", monospace', fontSize: '0.88rem' }}>
                        {GHS} {fmt(totalPetty)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          </Box>
        )}

        {approvedExpenses.length === 0 && !pettyLoading && (
          <Box mt={2} sx={{ textAlign: 'center', py: 3, borderRadius: 2, border: '1px dashed', borderColor: alpha(PRIMARY, 0.2) }}>
            <Typography variant="caption" color="text.disabled">No approved petty expenses for this period</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}


function UserAccount() {
  const theme = useTheme();
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  const [searchText, setSearchText] = useState('');
  const [selectedCashier, setSelectedCashier] = useState('all');
  const [selectedIncomeCategory, setSelectedIncomeCategory] = useState('all');
  const [selectedNarration, setSelectedNarration] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [sampleTransactions, setSampleTransactions] = useState([]);
  const [pettyExpenses, setPettyExpenses] = useState([]);

  
  const loadDayIncome = async () => {
    setLoading(true);
    try {
      const currentDate = dayjs().format('YYYY-MM-DD');
      const dat1 = (startDate && startDate.format('YYYY-MM-DD')) || currentDate;
      const dat2 = (endDate && endDate.format('YYYY-MM-DD')) || currentDate;

      const response = await axios.post(DayIncome, { dat1, dat2 });

      if (response.data.success) {
        const normalized = response.data.results.map(t => ({
          ...t,
          incomeType: (t.incomeType || '').trim().toLowerCase(),
          narration: (t.narration || '').trim(),
          paymentMode: (t.paymentMode || '').trim(),
        }));
        setSampleTransactions(normalized);
        setPettyExpenses(response.data.pettyExpenses || []);
      }
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to Load Data',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDayIncome();
  }, []);

  
  const transactionsPerPage = 5;

  const cashiers = useMemo(() => {
    const uniqueCashiers = [...new Set(sampleTransactions.map(t => JSON.stringify({id: t.cashierId, name: t.cashierName})))]
      .map(str => JSON.parse(str));
    return uniqueCashiers;
  }, [sampleTransactions]);

  const incomeCategories = useMemo(() => {
    const unique = [...new Set(sampleTransactions.map(t => t.incomeType))];
    return unique.sort();
  }, [sampleTransactions]);

  const narrations = useMemo(() => {
    let filteredData = sampleTransactions;
    if (selectedIncomeCategory !== 'all') {
      filteredData = sampleTransactions.filter(
        t => t.incomeType?.trim().toLowerCase() === selectedIncomeCategory.trim().toLowerCase()
      );
    }
    const uniqueNarrations = [...new Set(filteredData.map(t => t.narration))].filter(Boolean);
    return uniqueNarrations.sort();
  }, [sampleTransactions, selectedIncomeCategory]);

  useEffect(() => {
    setSelectedNarration('all');
  }, [selectedIncomeCategory]);

  const filteredTransactions = useMemo(() => {
    return sampleTransactions.filter((transaction) => {
      const transactionDate = dayjs(transaction.dat);
      const isWithinDateRange =
        transactionDate.isValid() &&
        transactionDate.isBetween(startDate, endDate, 'day', '[]');
      const searchLower = searchText.toLowerCase();
      const matchesSearch =
        !searchText ||
        transaction.patientId?.toLowerCase().includes(searchLower) ||
        transaction.patientName?.toLowerCase().includes(searchLower);
      const matchesCashier   = selectedCashier === 'all' || transaction.cashierId === selectedCashier;
      const matchesCategory  = selectedIncomeCategory === 'all' || transaction.incomeType === selectedIncomeCategory;
      const matchesNarration = selectedNarration === 'all' || transaction.narration === selectedNarration;
      return isWithinDateRange && matchesSearch && matchesCashier && matchesCategory && matchesNarration;
    });
  }, [sampleTransactions, startDate, endDate, searchText, selectedCashier, selectedIncomeCategory, selectedNarration]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, selectedCashier, selectedIncomeCategory, selectedNarration, startDate, endDate]);

  const validTransactions = filteredTransactions;

  const totalPages   = Math.ceil(validTransactions.length / transactionsPerPage);
  const startIndex   = (currentPage - 1) * transactionsPerPage;
  const paginatedTransactions = validTransactions.slice(startIndex, startIndex + transactionsPerPage);

  const paymentModeSummary = useMemo(() => {
    const grouped = validTransactions.reduce((acc, transaction) => {
      const mode = transaction.paymentMode;
      if (!acc[mode]) acc[mode] = { count: 0, total: 0 };
      acc[mode].count += 1;
      acc[mode].total += (transaction.amount - (transaction.reversal || 0));
      return acc;
    }, {});
    return Object.entries(grouped).map(([mode, data]) => ({ mode, count: data.count, total: data.total }));
  }, [validTransactions]);

  const cashierSummary = useMemo(() => {
    const grouped = validTransactions.reduce((acc, transaction) => {
      const cashierId = transaction.cashierId;
      if (!acc[cashierId]) acc[cashierId] = { name: transaction.cashierName, count: 0, total: 0 };
      acc[cashierId].count += 1;
      acc[cashierId].total += (transaction.amount - (transaction.reversal || 0));
      return acc;
    }, {});
    return Object.entries(grouped).map(([cashierId, data]) => ({ cashierId, name: data.name, count: data.count, total: data.total }));
  }, [validTransactions]);

  const handlePageChange = (event, value) => { setCurrentPage(value); };

  const getFilterDescription = () => {
    let description = `Showing ${validTransactions.length} transactions`;
    if (selectedCashier !== 'all') {
      const selectedCashierName = cashiers.find(c => c.id === selectedCashier)?.name;
      description += ` for ${selectedCashierName}`;
    }
    if (selectedIncomeCategory !== 'all') description += ` in category "${selectedIncomeCategory}"`;
    if (selectedNarration !== 'all') description += ` with narration "${selectedNarration}"`;
    if (searchText) description += ` matching "${searchText}"`;
    description += ` between ${startDate.format('MMM DD, YYYY')} and ${endDate.format('MMM DD, YYYY')}`;
    return description;
  };

  const grossTotal   = validTransactions.reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
  const totalReversal = validTransactions.reduce((sum, t) => sum + (Number(t.reversal) || 0), 0);
  const netTotal     = grossTotal - totalReversal;

  const totalExpenses = pettyExpenses
  .filter(exp => exp.status === 'approved')
  .reduce((sum, exp) => sum + (Number(exp.amount) || 0), 0);

  const finalNet = netTotal - totalExpenses;


  const creditTotal = validTransactions
    .filter(t => t.paymentMode.toLowerCase() === 'credit')
    .reduce((sum, t) => sum + Number(t.amount || 0), 0);

  const cashCollected = validTransactions
    .filter(t => t.paymentMode.toLowerCase() !== 'credit')
    .reduce((sum, t) => sum + Number(t.amount || 0), 0);


  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Payment sx={{ fontSize: 40 }} />
              <Typography variant="h3" component="h4" sx={{ fontWeight: 500, fontSize: { xs: '2rem', md: '3rem' } }}>
                Account Balancing
              </Typography>
            </Box>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 200 }}>
              Monitor and analyze patient billing transactions by cashier
            </Typography>
          </Box>

          {/* Filters Section */}
          <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 3, background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <CalendarToday />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Filter Transactions</Typography>
            </Box>
            <Grid container spacing={3} columns={{ xs: 4, sm: 8, md: 12 }}>
              <Grid item xs={12} sm={6} md={2} size={2}>
                <DatePicker label="Start Date" value={startDate} onChange={(newValue) => newValue && setStartDate(newValue)}
                  slotProps={{ textField: { fullWidth: true, variant: 'outlined', sx: { '& .MuiOutlinedInput-root': { borderRadius: 2, transition: 'all 0.2s', '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } } } } }} />
              </Grid>
              <Grid item xs={12} sm={6} md={2} size={2}>
                <DatePicker label="End Date" value={endDate} onChange={(newValue) => newValue && setEndDate(newValue)}
                  slotProps={{ textField: { fullWidth: true, variant: 'outlined', sx: { '& .MuiOutlinedInput-root': { borderRadius: 2, transition: 'all 0.2s', '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } } } } }} />
              </Grid>
              <Grid item xs={12} sm={6} md={2} size={2}>
                <FormControl fullWidth variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, transition: 'all 0.2s', '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } } }}>
                  <InputLabel>Income Category</InputLabel>
                  <Select value={selectedIncomeCategory} onChange={(e) => setSelectedIncomeCategory(e.target.value)} label="Income Category" startAdornment={<Category sx={{ mr: 1, color: 'action.active' }} />}>
                    <MenuItem value="all">All Categories</MenuItem>
                    {incomeCategories.map((category) => <MenuItem key={category} value={category}>{category}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2} size={2}>
                <FormControl fullWidth variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, transition: 'all 0.2s', '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } } }}>
                  <InputLabel>Narration</InputLabel>
                  <Select value={selectedNarration} onChange={(e) => setSelectedNarration(e.target.value)} label="Narration" startAdornment={<Assignment sx={{ mr: 1, color: 'action.active' }} />}>
                    <MenuItem value="all">All Narrations</MenuItem>
                    {narrations.map((narration) => <MenuItem key={narration} value={narration}>{narration}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2} size={2}>
                <Button fullWidth variant="contained" onClick={loadDayIncome} disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <CloudDownload />}
                  sx={{ height: '56px', borderRadius: 2, textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)', boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)', background: 'linear-gradient(135deg, #43a047 0%, #66bb6a 100%)' }, '&:disabled': { background: 'linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%)', boxShadow: 'none', transform: 'none' } }}>
                  {loading ? 'Loading...' : 'Load Data'}
                </Button>
              </Grid>
              <Grid item xs={12} md={2} size={2}>
                <FormControl fullWidth variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, transition: 'all 0.2s', '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } } }}>
                  <InputLabel>Cashier/User</InputLabel>
                  <Select value={selectedCashier} onChange={(e) => setSelectedCashier(e.target.value)} label="Cashier/User" startAdornment={<Person sx={{ mr: 1, color: 'action.active' }} />}>
                    <MenuItem value="all">All Cashiers</MenuItem>
                    {cashiers.map((cashier) => <MenuItem key={cashier.id} value={cashier.id}>{cashier.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2} size={2}>
                <TextField fullWidth label="Search Patient" value={searchText} onChange={(e) => setSearchText(e.target.value)} variant="outlined" placeholder="Enter patient ID or name..."
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, transition: 'all 0.2s', '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } } }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search color="action" /></InputAdornment> }} />
              </Grid>
            </Grid>
          </Paper>

          {(searchText || selectedCashier !== 'all' || selectedIncomeCategory !== 'all' || selectedNarration !== 'all') && (
            <Alert severity="info" sx={{ mb: 3, borderRadius: 3, '& .MuiAlert-icon': { fontSize: '1.5rem' } }}>
              {getFilterDescription()}
            </Alert>
          )}

          {validTransactions.length > 0 && (
            <Grid container spacing={3} sx={{ mb: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
              <Grid item xs={12} lg={4} size={4}>
                <Card elevation={3} sx={{ borderRadius: 3, height: '100%', background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 24px rgba(0,0,0,0.15)' } }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                      <Payment />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>Payment Mode Summary</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {paymentModeSummary.map((item) => (
                        <Paper key={item.mode} elevation={1} sx={{ p: 2, borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 16px rgba(0,0,0,0.1)' } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label={item.mode} size="small" variant="outlined" sx={{ borderColor: getPaymentModeColor(item.mode), color: getPaymentModeColor(item.mode), fontWeight: 600 }} />
                            <Typography variant="body2" color="text.secondary">({item.count})</Typography>
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>{formatCurrency(item.total)}</Typography>
                        </Paper>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} lg={5} size={5}>
                <Card elevation={3} sx={{ borderRadius: 3, height: '100%', background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 24px rgba(0,0,0,0.15)' } }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                      <TrendingUp />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>Cashier Performance</Typography>
                    </Box>
                    {selectedCashier === 'all' && cashierSummary.length > 1 ? (
                      <Box sx={{ height: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={cashierSummary}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} fontSize={12} interval={0} />
                            <YAxis tickFormatter={(value) => `${(value/1000).toFixed(1)}k`} fontSize={12} />
                            <RechartsTooltip formatter={(value) => [formatCurrency(Number(value)), 'Revenue']} labelStyle={{ color: '#374151' }} contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                            <Bar dataKey="total" radius={[4, 4, 0, 0]}>
                              {cashierSummary.map((entry, index) => {
                                const colors = ['#4f46e5','#06b6d4','#10b981','#f59e0b','#ef4444','#8b5cf6','#f97316','#84cc16','#ec4899','#6366f1','#14b8a6','#f43f5e'];
                                return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                              })}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    ) : (
                      <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: 'text.secondary' }}>
                        <Group sx={{ fontSize: 48, mb: 2, color: 'action.disabled' }} />
                        <Typography variant="body1" align="center">Select "All Cashiers" to view performance chart</Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} lg={3} size={3}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
                  {selectedCashier === 'all' && cashierSummary.length > 1 && (
                    <Card elevation={3} sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 24px rgba(0,0,0,0.15)' } }}>
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <Person sx={{ fontSize: 20 }} />
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Quick Summary</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {cashierSummary.slice(0, 3).map((item) => (
                            <Box key={item.cashierId} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>{item.name}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main', fontSize: '0.75rem' }}>{formatCurrency(item.total)}</Typography>
                            </Box>
                          ))}
                          {cashierSummary.length > 3 && (
                            <Typography variant="caption" color="text.secondary" align="center" sx={{ pt: 1 }}>+{cashierSummary.length - 3} more</Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  )}
                  <Card elevation={3} sx={{ borderRadius: 3, flexGrow: 1, background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)', color: 'white', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 24px rgba(76, 175, 80, 0.3)' } }}>
                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
      <DoneAllIcon sx={{ fontSize: 28 }} />
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Accounting Summary
      </Typography>
    </Box>

    <Typography variant="body2" sx={{ opacity: 0.9 }}>
      Gross Revenue
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 700 }}>
      {formatCurrency(grossTotal)}
    </Typography>

    <Typography variant="body2" sx={{ mt: 2, opacity: 0.9 }}>
      Total Reversal
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 700, color: '#ffebee' }}>
      - {formatCurrency(totalReversal)}
    </Typography>

    <Typography variant="body2" sx={{ mt: 2, opacity: 0.9 }}>
      Total Expenses
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff3e0' }}>
      - {formatCurrency(totalExpenses)}
    </Typography>

    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.3)' }}>
      <Typography variant="body2" sx={{ opacity: 0.9 }}>
        Net Revenue
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 800 }}>
        {formatCurrency(finalNet)}
      </Typography>
    </Box>

    <Typography variant="caption" sx={{ opacity: 0.8 }}>
      {validTransactions.length} transactions
    </Typography>
  </CardContent>
                  </Card>
                </Box>
              </Grid>
            </Grid>
          )}

          {/* Transaction Details Table */}
          <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ p: 3, bgcolor: alpha(theme.palette.grey[100], 0.8), borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Transaction Details</Typography>
                {(selectedIncomeCategory !== 'all' || selectedNarration !== 'all') && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary">Active Filters:</Typography>
                    {selectedIncomeCategory !== 'all' && (
                      <Chip label={`${selectedIncomeCategory} (${validTransactions.length})`} size="small" sx={{ bgcolor: getIncomeTypeColor(selectedIncomeCategory), color: 'white', fontWeight: 600 }} onDelete={() => setSelectedIncomeCategory('all')} />
                    )}
                    {selectedNarration !== 'all' && (
                      <Chip label={selectedNarration} size="small" sx={{ bgcolor: '#607d8b', color: 'white', fontWeight: 600 }} onDelete={() => setSelectedNarration('all')} />
                    )}
                  </Box>
                )}
              </Box>
              {totalPages > 0 && <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} color="primary" size="small" />}
            </Box>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Date','Receipt ID','Narration','Income Type','Amount','Reversal','Payment Mode','Patient Name','Cashier'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.grey[200], 0.8), color: 'text.primary', borderBottom: 2, borderColor: 'divider', ...(h==='Amount'||h==='Reversal' ? {textAlign:'right'} : {}) }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {validTransactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ textAlign: 'center', py: 6 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                          <Description sx={{ fontSize: 48, color: 'action.disabled' }} />
                          <Typography variant="h6" color="text.secondary">No transactions found for the selected criteria</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedTransactions.map((transaction, index) => (
                  <TableRow 
                  key={`${transaction.transactionId}-${transaction.incomeType}-${index}`}
                  sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04), transform: 'scale(1.001)' }, transition: 'all 0.2s', bgcolor: index % 2 === 0 ? 'background.default' : alpha(theme.palette.grey[50], 0.5) }}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{dayjs(transaction.dat).format('MMM DD, YYYY')}</Typography>
                      <Typography variant="caption" color="text.secondary">{dayjs(transaction.timestamp).format('hh:mm A')}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{transaction.transactionId}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{transaction.narration}</Typography></TableCell>
                  <TableCell><Chip label={transaction.incomeType.toUpperCase()} size="small" sx={{ bgcolor: getIncomeTypeColor(transaction.incomeType), color: 'white', fontWeight: 600 }} /></TableCell>
                  <TableCell sx={{ textAlign: 'right' }}><Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>{formatWithOutSom(transaction.amount)}</Typography></TableCell>
                  <TableCell sx={{ textAlign: 'right' }}><Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main' }}>{formatWithOutSom(transaction.reversal)}</Typography></TableCell>
                  <TableCell><Chip label={transaction.paymentMode} size="small" variant="outlined" sx={{ borderColor: getPaymentModeColor(transaction.paymentMode), color: getPaymentModeColor(transaction.paymentMode), fontWeight: 600 }} /></TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{transaction.patientId}</Typography>
                      <Typography variant="caption" color="text.secondary">{transaction.patientName}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{transaction.cashierName}</Typography></TableCell>
                </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* ── PETTY EXPENSES & BALANCE OFF SECTION ── */}
          <PettyBalanceSection
            netTotal={netTotal}
            pettyExpenses={pettyExpenses}
            pettyLoading={loading}
          />

        </Container>
      </Box>
    </LocalizationProvider>
  );
}

export default UserAccount;